import type { RequestPayload } from '@inertiajs/core';
import { router } from '@inertiajs/react';
import { useState } from 'react';

// The stage server actions answered `{ ok: true } | { ok: false, error }` and every
// component that fired one kept a `useTransition` pending flag and an `error` string
// next to it. The controller redirects back with the same message on `errors.base`, so
// this is that pair of hooks, once.
//
// `preserveState` is what keeps the editor's optimistic order and the open dialogs
// alive across the round trip — `revalidatePath` re-rendered the server component
// without remounting the client ones.
export function useStageAction() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = (
    method: 'post' | 'patch' | 'delete',
    url: string,
    data: RequestPayload = {},
    onOk?: () => void,
  ) => {
    setPending(true);
    setError(null);
    router.visit(url, {
      method,
      data,
      preserveScroll: true,
      preserveState: true,
      // The controller writes one string per key; `errorValueType` types them as arrays.
      onError: (errors) =>
        setError(
          (errors as unknown as Record<string, string | undefined>).base ??
            'Something went wrong',
        ),
      onSuccess: () => onOk?.(),
      onFinish: () => setPending(false),
    });
  };

  return { pending, error, setError, run };
}
